import React, { useMemo } from 'react';
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { timeFormat } from 'd3-time-format';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Line, LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Grid } from '@visx/grid';
import { Axis } from '@visx/visx';
import { Box } from '@mui/material';
import { useParentWidthAndHeight } from './use-parent-width-and-height';
import { LegendItem, LegendLabel, LegendOrdinal } from '@visx/legend';
import { formatFloat } from '../../../../helpers';

type Data = [number, number];

const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const getXValue = (d: Data) => new Date(d[0]);

const getYValue = (d: Data) => d[1];

// const bisectDate = bisector<Data, Date>(getXValue).left;

const tooltipStyles = {
    ...defaultStyles,
    borderRadius: 4,
    background: "#161434",
    color: "#ADADD3",
    fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
};

export type ILineChart<T> = {
    getX: (item: T) => string;
    getY: (item: T) => number;
    dataSets: DataSet<T>[]
}

export type DataSet<T> = {
    name: string;
    color: string;
    data: T[];
}


export function LineChart<T>  (props : ILineChart<T>) {
    const { parentRef, width, height } = useParentWidthAndHeight();

    return (
        <Box ref={parentRef} sx={{ height: '100%'}} >
            <LineChartChild {...props } width={width} height={height} />
        </Box>
    );

}

const margin = { top: 30, right: 100, bottom: 30, left: 100 };

const legendGlyphSize = 8

export function LineChartChild<T>  ({ width: propsWidth, height: propsHeight, getY, getX, dataSets} : ILineChart<T> & { width: number; height: number;}) {
    const data: T[] = useMemo(() => {
        return dataSets.length > 0 ?
            dataSets[0].data : []
    }, [dataSets])


    const {

        tooltipData,
        tooltipLeft = 0,
        tooltipTop = 0,
    } = useTooltip<Data>();

    var width = propsWidth - margin.left - margin.right;
    var height = propsHeight - margin.top - margin.bottom;


    const xScale = scaleBand<string>({
        range: [0, width],
        domain: data.map(getX),
    });

    const yScale = scaleLinear<number>({
        range: [height, 0],
        round: true,
        domain: [
            500000,
            15000000,
        ],
        nice: true,
    });

    const colorScale = scaleOrdinal({
        domain: dataSets.map(dataSet => dataSet.name),
        range: dataSets.map(dataSet => dataSet.color)
    });

    // what is the meaning of 'viewBox={`0 0 ${width} ${height}`}'

    // get youtuber trick to use parent height, make it responsive.

    return (
        <>
            <Group
            >
                <LegendOrdinal style={{width: '200px'}} shapeWidth={200} scale={colorScale} labelFormat={(label) => `${String(label).toUpperCase()}`}>
                    {(labels) => (
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "center"}}>
                            {labels.map((label, i) => (
                                <LegendItem
                                    key={`legend-quantile-${i}`}
                                    margin="0 5px"
                                    onClick={() => {
                                        // if (events) alert(`clicked: ${JSON.stringify(label)}`);
                                    }}
                                >
                                    <svg width={legendGlyphSize} height={legendGlyphSize}>
                                        <rect fill={label.value} width={legendGlyphSize} height={legendGlyphSize} />
                                    </svg>
                                    <LegendLabel align="left" margin="0 0 0 4px" >
                                        <span style={{ color: 'black' }}>{label.text.toLowerCase()}</span>
                                    </LegendLabel>
                                </LegendItem>
                            ))}
                        </div>
                    )}
                </LegendOrdinal>
            </Group>
            <svg
                width={width + margin.left + margin.right}
                height={height + margin.top + margin.bottom}
            >

                <Group left={margin.left} top={margin.top}>
                    {dataSets.map(dataSet => {
                        return (
                            <LinePath<T>
                                data={dataSet.data}
                                x={(d) => xScale(getX(d)) ?? 0}
                                y={(d) => yScale(getY(d)) ?? 0}
                                stroke={dataSet.color}
                                strokeWidth={2}
                                curve={curveMonotoneX}
                            />
                        )
                    })}
                </Group>

                <Grid
                    xScale={xScale}
                    yScale={yScale}
                    width={width}
                    height={height}
                    left={margin.left}
                    top={margin.top}
                />

                {/*<Group>*/}
                {/*    <Bar*/}
                {/*        width={width}*/}
                {/*        height={height}*/}
                {/*        fill="transparent"*/}
                {/*        onMouseMove={(*/}
                {/*            event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>*/}
                {/*        ) => {*/}
                {/*            const { x } = localPoint(event) || { x: 0 };*/}
                {/*            const x0 = xScale.invert(x);*/}
                {/*            const index = bisectDate(data, x0, 1);*/}
                {/*            const d0 = data[index - 1];*/}
                {/*            const d1 = data[index];*/}
                {/*            let d = d0;*/}
                {/*            if (d1 && getXValue(d1)) {*/}
                {/*                d =*/}
                {/*                    x0.valueOf() - getXValue(d0).valueOf() >*/}
                {/*                    getXValue(d1).valueOf() - x0.valueOf()*/}
                {/*                        ? d1*/}
                {/*                        : d0;*/}
                {/*            }*/}
                {/*            showTooltip({*/}
                {/*                tooltipData: d,*/}
                {/*                tooltipLeft: x,*/}
                {/*                tooltipTop: yScale(getYValue(d)),*/}
                {/*            });*/}
                {/*        }}*/}
                {/*        onMouseLeave={() => hideTooltip()}*/}
                {/*    />*/}
                {/*</Group>*/}

                {tooltipData ? (
                    <Group>
                        <Line
                            from={{ x: tooltipLeft, y: 0 }}
                            to={{ x: tooltipLeft, y: height }}
                            stroke="#59588D"
                            strokeWidth={1}
                            pointerEvents="none"
                            strokeDasharray="5, 5"
                        />
                        <circle
                            cx={tooltipLeft}
                            cy={tooltipTop}
                            r={8}
                            fill="#FF4DCA"
                            fillOpacity={0.5}
                            pointerEvents="none"
                        />
                        <circle
                            cx={tooltipLeft}
                            cy={tooltipTop}
                            r={4}
                            fill="#FF4DCA"
                            pointerEvents="none"
                        />
                    </Group>
                ) : null}

                <Axis.AxisBottom
                    left={margin.left}
                    top={height + margin.top}
                    scale={xScale}
                    tickFormat={(d) => {
                        return d
                    }}
                    stroke={ "black"}
                    tickStroke={ "black"}
                    tickLabelProps={{
                        fill:  "black",
                        fontSize: 11,
                        textAnchor: 'middle',

                    }}
                    numTicks={data.length}
                />

                <Axis.AxisLeft
                    scale={yScale}
                    left={margin.left}
                    top={margin.top}
                    tickFormat={(d) => {
                        return formatFloat(d)
                    }}
                    stroke={ "black"}
                    tickStroke={ "black"}
                    tickLabelProps={{
                        fill: "black",
                        fontSize: 11,
                        textAnchor: 'middle',
                        transform: 'translate(-40, 0)'
                    }}
                />

            </svg>



            {tooltipData ? (
                <TooltipWithBounds
                    key={Math.random()}
                    top={tooltipTop}
                    left={tooltipLeft + margin.left}
                    style={tooltipStyles}
                >
                    {`${timeFormat("%b %d %H:%M ")(new Date(getXValue(tooltipData)))}`}:{" "}
                    <b>{formatter.format(getYValue(tooltipData))}</b>
                </TooltipWithBounds>
            ) : null}



        </>
    );
};

export default LineChart;
